import React, { FC } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { AppContextConsumer } from '../../contexts/AppContext';
import { IsOrgAdminOrBlockitAdmin } from '../../../../components/IsOrgAdminOrBlockitAdmin';
import { FeatureFlag, FeatureGate } from '../../../../helpers';
import { Viewer, ViewerOrganization } from '../../../viewer';
import config from '../../../../config';
import { useBlockitThemeContext } from '../../../../themes/ThemeContext';

interface NavProps {
  organization: ViewerOrganization | null;
  mode: string;
  viewer?: Viewer | null;
}

const Styles = styled.div<{
  linkColor?: string;
  activeBorderColor?: string;
  activeBorderWidth?: string;
  isAlluvium?: boolean;
}>`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  padding-top: ${({ isAlluvium }) => (isAlluvium ? '0px' : '1.8rem')};
  align-items: ${({ isAlluvium }) => (isAlluvium ? 'flex-end' : 'initial')};

  a,
  a:visited,
  .is-super-user {
    border-bottom: ${({ activeBorderWidth }) => activeBorderWidth || '5px'} solid transparent;
    color: ${({ linkColor }) => linkColor || '#fff'};
    display: block;
    font-size: ${({ isAlluvium }) => (isAlluvium ? '16px' : '20px')};
    padding-bottom: ${({ isAlluvium }) => (isAlluvium ? '0.9rem' : '15px')};
    padding-top: ${({ isAlluvium }) => (isAlluvium ? '0.9rem' : '0px')};
    text-decoration: none;
    margin-left: 30px;

    &:first-child {
      margin-left: 0;
    }

    &.active,
    &:hover {
      border-bottom-color: ${({ activeBorderColor }) => activeBorderColor || '#fff'};
    }

    &.active {
      font-weight: 700;
    }
  }
`;

const ReferralsNav: FC<NavProps> = ({ organization }): JSX.Element | null =>
  organization && (
    <>
      <FeatureGate feature={FeatureFlag.HasSchedulePatient}>
        <NavLink to={`/referrals/new`} exact>
          Schedule a Patient
        </NavLink>
      </FeatureGate>
      <NavLink to={`/organizations/${organization.id}/referrals`} exact>
        Referrals
      </NavLink>
      <FeatureGate feature={FeatureFlag.ConsumerScheduling}>
        <NavLink to={`/organizations/${organization.id}/appointments`} exact>
          Appointments
        </NavLink>
      </FeatureGate>
      <FeatureGate feature={FeatureFlag.HasProviderDirectory}>
        <NavLink to={`/directory`} exact>
          Provider Directory
        </NavLink>
      </FeatureGate>
      <FeatureGate feature={FeatureFlag.HasPatientDirectory}>
        <a href={`${config.endpoint}/patient-directory?organization_id=${organization.id}`}>Patient Directory</a>
      </FeatureGate>
      <FeatureGate feature={FeatureFlag.HasPatientItinerary}>
        <a href={`${config.endpoint}/itineraries?organization_id=${organization.id}`}>View Itineraries</a>
      </FeatureGate>
      <FeatureGate
        feature={FeatureFlag.WorkflowQueuesV2}
        fallbackContent={
          <NavLink to={`/organizations/${organization.id}/workflows`} exact>
            Workflow Queues
          </NavLink>
        }
      >
        <a href={`${config.endpoint}/workflows?organization_id=${organization.id}`}>Workflow Queues</a>
      </FeatureGate>
      <IsOrgAdminOrBlockitAdmin>
        <FeatureGate feature={FeatureFlag.DataAnalytics}>
          <a href={`${config.endpoint}/data-analytics/referral/organization?organization_id=${organization.id}`}>
            Dashboard
          </a>
        </FeatureGate>
      </IsOrgAdminOrBlockitAdmin>
    </>
  );

const AdminNav: FC<NavProps> = ({ organization, viewer }): JSX.Element | null => {
  const isProfileLocationOnly = viewer?.profileLocationOnly || false;

  return organization ? (
    <>
      {!isProfileLocationOnly && (
        <IsOrgAdminOrBlockitAdmin>
          <NavLink to={`/organizations/${organization.id}/organization-settings`} exact>
            Organization
          </NavLink>
        </IsOrgAdminOrBlockitAdmin>
      )}

      <FeatureGate
        feature={FeatureFlag.EnablePdmV2}
        fallbackContent={<NavLink to={`/organizations/${organization.id}/profiles`}>Profiles</NavLink>}
      >
        <a href={`${config.endpoint}/organizations/${organization.id}/profiles`}>Profiles</a>
      </FeatureGate>

      {!isProfileLocationOnly && (
        <IsOrgAdminOrBlockitAdmin>
          <>
            <NavLink to={`/organizations/${organization.id}/providers`}>Providers</NavLink>
            <NavLink to={`/organizations/${organization.id}/users`}>Users</NavLink>
          </>
        </IsOrgAdminOrBlockitAdmin>
      )}

      <IsOrgAdminOrBlockitAdmin>
        <FeatureGate
          feature={FeatureFlag.EnablePdmV2}
          fallbackContent={<NavLink to={`/organizations/${organization.id}/locations`}>Location</NavLink>}
        >
          <a href={`${config.endpoint}/organizations/${organization.id}/locations`}>Location</a>
        </FeatureGate>
      </IsOrgAdminOrBlockitAdmin>

      {!isProfileLocationOnly && (
        <>
          <IsOrgAdminOrBlockitAdmin>
            <FeatureGate feature={FeatureFlag.HasNetworkGroups}>
              <NavLink to={`/organizations/${organization.id}/groups`}>Network Groups</NavLink>
            </FeatureGate>
          </IsOrgAdminOrBlockitAdmin>
          <FeatureGate feature={FeatureFlag.ProviderManagement}>
            <a href={`${config.endpoint}/provider-management?organization_id=${organization.id}`}>
              Provider Management
            </a>
          </FeatureGate>
          <FeatureGate feature={FeatureFlag.ConsumerScheduling}>
            <a href={`${config.endpoint}/organizations/${organization.id}/directories`}>Directories</a>
          </FeatureGate>
        </>
      )}
    </>
  ) : null;
};

export const AppNav = (): JSX.Element => {
  const { isAlluvium, branding } = useBlockitThemeContext();
  const linkColor = isAlluvium ? branding.darkAccent : undefined;
  const activeBorderColor = isAlluvium ? branding.darkAccent : undefined;
  const activeBorderWidth = isAlluvium ? '3px' : undefined;

  return (
    <AppContextConsumer>
      {({ currentOrganization, mode, viewer }) => {
        const isProfileLocationOnly = viewer?.profileLocationOnly || false;
        const currentMode = isProfileLocationOnly ? 'admin' : mode;

        return (
          <Styles
            linkColor={linkColor}
            activeBorderColor={activeBorderColor}
            activeBorderWidth={activeBorderWidth}
            isAlluvium={isAlluvium}
          >
            {currentMode === 'referral' ? (
              <ReferralsNav organization={currentOrganization} mode={'referral'} viewer={viewer} />
            ) : (
              <AdminNav organization={currentOrganization} mode={'admin'} viewer={viewer} />
            )}
          </Styles>
        );
      }}
    </AppContextConsumer>
  );
};
